import { POSTHOG_HOST } from './config';
import posthog from 'posthog-js';

// Every call below is gated on the key: unset → PostHog stays off. NEXT_PUBLIC_*
// is read from process.env (statically inlined by Next at build; settings.ts is
// server-only, so it can't be read here).
const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;

export function initPostHog(): void {
  if (!key) return;
  posthog.init(key, {
    api_host: POSTHOG_HOST,
    // No anonymous profiles — one is created only when identifyUser() runs.
    person_profiles: 'identified_only',
    // App Router navigations are client-side; capture pageviews on History API
    // changes so route transitions are recorded without a manual tracker.
    capture_pageview: 'history_change',
    // Session replay ships the DOM, and this console renders SSNs, participant
    // PII, and payroll figures. Mask every input value AND all rendered text so
    // no readable content leaves the browser. Recording must also be enabled in
    // the PostHog project settings for capture to start.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },
  });
}

export function identifyUser(user: {
  userId: string;
  email: string;
  name: string | null;
  role: string;
}): void {
  if (!key) return;
  posthog.identify(user.userId, { email: user.email, name: user.name ?? undefined, role: user.role });
}

// Registers the workspace group so every event this browser sends is filterable
// by tenant — this is what lets us view one customer (e.g. KPC) in isolation.
export function setWorkspaceGroup(workspace: {
  id: string;
  name: string;
  slug: string;
  isKpc: boolean;
}): void {
  if (!key) return;
  posthog.group('workspace', workspace.id, {
    name: workspace.name,
    slug: workspace.slug,
    is_kpc: workspace.isKpc,
  });
}

// Client interactions not visible from a pageview or an audit event. Never pass
// PII (message text, names, SSNs) in `properties` — see agent_docs/pii-handling.md.
export type ClientEventName = 'chat_message_sent';

export function trackClientEvent(
  name: ClientEventName,
  properties?: Record<string, string | number | boolean>,
): void {
  if (!key) return;
  posthog.capture(name, properties);
}
